/**
 * Character API Service
 *
 * File: Mythbound.App/src/services/api/CharacterService.ts
 * Created: 2026-01-06
 * Last Modified: 2026-09-21 (PRD001A 001A.4 — Character contract half)
 *
 * PURPOSE:
 * HTTP client mirroring the FROZEN backend Character contract exactly:
 * `shared/contracts/character/v1/character-management.yaml` (v2.0.0) and
 * its source of truth, `src/api/routers/characters_enhanced.py` (backend
 * worktree, read-only reference for this wave — see
 * `001A_BE_Checkpoint.md`'s "Wave 4: 001A.4 BE" section and its "FE contract
 * summary").
 *
 * WHO: `CharacterContext.tsx` owns campaign roster reads and mutations.
 * `CharacterCreationModal.tsx` and `SessionView.tsx` also call this client
 * directly. PRD004.1 makes `CharacterRosterSidebar.tsx` consume the page
 * provider roster; it no longer maintains an independent service request.
 * WHAT: Types mirror the contract's nullable-vs-omitted and owner-vs-non-owner
 * distinctions exactly (see `Character`, `CharacterUpdateRequest` below).
 * `archiveCharacter`/`restoreCharacter` replace the old `deleteCharacter`
 * (DELETE {id} is not registered on the backend at all — 404 if called).
 * `getCharacterErrorCode` extracts the contract's `detail.code` from a
 * thrown `ApiError` so callers can distinguish `duplicate_name` from
 * `active_character_exists` from `lifecycle_conflict`, all of which share
 * HTTP 409 and would otherwise collapse to the same `ApiError.kind`.
 * WHY: The 001A.1 inventory found every method here built its own request
 * shape independent of any real backend model (e.g. `hp` instead of the
 * actual `hit_points` field, fabricated `Fighter`/`0`/`10` defaults). This
 * wave aligns every request/response type to the frozen contract instead.
 *
 * Ownership is conveyed only by the server-computed `is_owner` boolean.
 * Missing/invalid values are treated as not-owner by consumers; no user ID
 * or roster position is an ownership authority. Narrative fields are
 * optional projections and remain unavailable to non-owners.
 *
 * VISIBILITY FILTERING (R6, 2026-09-23 — matches the backend's
 * `owner_payload()`/`filtered_payload()` split in `characters_enhanced.py`
 * exactly; see `001A_R6_Projection_Checkpoint.md`):
 * - Character owner: sees the full authorized sheet, always — every
 *   projection-sensitive key is present with its real value (or `null` if
 *   genuinely never set). An owner's own value is NEVER hidden just because
 *   its own sharing flag happens to be `false` — the owner is never filtered.
 * - Every other viewer — INCLUDING the GM — sees only visibility-permitted
 *   fields. There is no GM bypass (removed 001A.3, unchanged 001A.4/R6).
 * - THREE distinct wire states for `hp`/`max_hp`/`armor_class` (and, in
 *   principle, `character_name`/`character_class`/`level`, though no real
 *   code path hides those today): the KEY IS ABSENT from the JSON entirely
 *   when the owner hid that field (parses to `undefined`); the key is
 *   PRESENT with `null` when visible but never set; the key is PRESENT with
 *   the real value (including `0`/`false`) when visible and set. Presence
 *   IS the hidden signal now — no more guessing from a `visibility` flag.
 *   Always check `undefined` BEFORE `null`. See `classifyCharacterField`
 *   below, which implements exactly this check.
 *
 * `character_class` (R2, 2026-09-23): now genuinely optional/nullable end
 * to end — the DB column is nullable with no server-side default (see
 * `src/database/migrations/001A3_character_class_nullable.sql`). A
 * character created or edited without a class returns `null` here, never a
 * fabricated `"Fighter"`. Do not treat a `character_class` of `"Fighter"` as
 * anything other than a genuinely stored value.
 *
 * Pipeline: Frontend components → CharacterService → Backend /api/characters → Database
 * Dependencies: authorizedFetch (cookie + bearer-if-fresh), ApiError
 */

import { authorizedFetch } from './authTransport';
import { ApiError, apiErrorFromResponse, apiErrorFromNetworkFailure } from './ApiError';
import { getApiBaseUrl } from '../../utils/environment';

/**
 * Per-field visibility flags. Every field is optional/boolean: `true` (or
 * omitted, per the backend's "default all-visible") means visible to other
 * campaign members; `false` means hidden from everyone except the owner.
 * Owner and GM are NOT treated specially by the backend when reading a
 * non-owner's character — only the owner ever bypasses this.
 */
export interface CharacterVisibility {
  hp?: boolean;
  armor_class?: boolean;
  speed?: boolean;
  strength?: boolean;
  dexterity?: boolean;
  constitution?: boolean;
  intelligence?: boolean;
  wisdom?: boolean;
  charisma?: boolean;
  skills?: boolean;
  spells?: boolean;
  equipment?: boolean;
}

/**
 * Mirrors `CharacterResponse` (characters_enhanced.py) exactly — the ONE
 * shape returned for both the owner projection (`owner_payload()`, full
 * sheet) and the non-owner/filtered projection (`filtered_payload()`). Only
 * the VALUES and KEY PRESENCE differ between the two; the type below
 * expresses both.
 *
 * `character_name`, `character_class`, `level`, `hp`, `max_hp`, and
 * `armor_class` are all `?: T | null` — the THREE-WAY distinction is load
 * bearing (R6, `001A_R6_Projection_Checkpoint.md`):
 * - key ABSENT (`undefined` once parsed): hidden by the owner's own
 *   visibility setting. Never happens for the owner's own view of their own
 *   character. `character_name`/`character_class`/`level` are always
 *   present for every viewer today (no visibility-request field exists for
 *   them yet) — but the type does not assume that can never change.
 * - key present with `null`: visible, but genuinely never set. Never a
 *   fabricated `0`/`10`/`"Fighter"`.
 * - key present with a real value, INCLUDING `0`/`false`/`""`: a genuine
 *   fact. `hp === 0` is a real, distinct value (an unconscious character)
 *   and must render as `0`, never as "unknown" or "hidden".
 *
 * ALWAYS check `undefined` before `null` when reading these fields — see
 * `classifyCharacterField` below, which does this once so callers don't
 * have to reimplement it.
 */
export interface Character {
  character_id: string;
  campaign_id: string;
  character_name?: string | null;
  character_class?: string | null;
  level?: number | null;
  hp?: number | null;
  max_hp?: number | null;
  armor_class?: number | null;
  created_at: string;
  visibility: CharacterVisibility;
  /** True when the stored row's status/is_active/is_archived/archived_at
   * contradict each other server-side (`Character.detect_lifecycle_conflict()`).
   * A non-redacting flag — render as a warning, not an error, on read. */
  lifecycle_conflict: boolean;
  /** Server-computed viewer ownership. Consumers must require `=== true`. */
  is_owner?: boolean;
  /** Owner-authorized narrative projection; omitted where not returned. */
  character_description?: string | null;
  character_preferences?: Record<string, unknown> | null;
  /** Canonical owner sheet data is detail/list-only and omitted for non-owners. */
  character_sheet?: Record<string, unknown> | null;
  /** Archived-inclusive roster results identify lifecycle state explicitly. */
  is_archived?: boolean;
}

/** How a single nullable Character field should be displayed. */
export type FieldDisplayKind = 'value' | 'unknown' | 'hidden';

export interface FieldDisplay<T> {
  kind: FieldDisplayKind;
  value: T | null;
}

/**
 * Classify a projection-sensitive `Character` field for display,
 * distinguishing "hidden by the owner's visibility setting" from
 * "genuinely never set" from "a real value" — the three states the R6
 * backend response actually puts on the wire (see module header and
 * `001A_R6_Projection_Checkpoint.md`). CHECK `undefined` BEFORE `null`:
 * an absent key (`undefined`) is the true hidden signal now, not a
 * `visibility` flag guess — `filtered_payload()` on the backend genuinely
 * omits the key when it's hidden, so presence alone tells the whole story.
 * Never returns a fabricated value; `kind: 'value'` is the only case that
 * has one.
 *
 * @param value The field's value from a `Character`, e.g. `character.hp` —
 *   `undefined` if the key was absent from the parsed JSON.
 * @param isOwner Whether the current viewer owns this character — an owner
 *   is only ever `'value'` or `'unknown'`, never `'hidden'`, because the
 *   backend never omits a key from the owner's own projection (`undefined`
 *   should not occur for an owner; if it somehow did, this treats it as
 *   `'unknown'` rather than fabricating a `'hidden'` state that contradicts
 *   "an authorized owner's real value must not be hidden by its own sharing
 *   flag").
 */
export function classifyCharacterField<T>(
  value: T | null | undefined,
  isOwner: boolean
): FieldDisplay<T> {
  if (value === undefined) return { kind: isOwner ? 'unknown' : 'hidden', value: null };
  if (value !== null) return { kind: 'value', value };
  return { kind: 'unknown', value: null };
}

/**
 * Render a classified field as display text. `formatValue` is only called
 * for `kind: 'value'` — callers never need their own fallback branches for
 * "hidden"/"not set", which is exactly how a fabricated `0`/`"Fighter"`/`10`
 * used to sneak back in (see the 001A.1 inventory).
 */
export function formatCharacterField<T>(display: FieldDisplay<T>, formatValue: (value: T) => string): string {
  if (display.kind === 'value') return formatValue(display.value as T);
  if (display.kind === 'hidden') return 'Hidden';
  return 'Not set';
}

/**
 * POST /create-quick request. `campaign_id`, `character_name`, and `level`
 * are the only required Character facts (R2, 2026-09-23 —
 * `characters_enhanced.py::CharacterQuickCreateRequest.character_class` is
 * now `Optional[str] = Field(None, ...)`). `character_class` is genuinely
 * optional to submit: OMIT the key entirely (or send an explicit `null`) to
 * store an unknown class — never a silently-fabricated default. A
 * submitted non-null value still must be a recognized D&D 5e class name or
 * the backend rejects it with a 422 `validation` error.
 * `hit_points` (not `hp`) is the actual field name; if omitted, HP remains
 * unknown, even when a class is provided.
 */
export interface CharacterQuickCreateRequest {
  campaign_id: string;
  character_name: string;
  character_class?: string;
  level: number;
  hit_points?: number;
  character_description?: string | null;
  character_preferences?: Record<string, unknown> | null;
}

export interface CharacterHPUpdateRequest {
  hp: number;
  max_hp?: number;
}

/** All fields optional; only provided keys change. Same shape as `CharacterVisibility`. */
export type CharacterVisibilityRequest = CharacterVisibility;

/**
 * PUT /{id} request. OMITTED-VS-NULL is the entire point of this type:
 * - A key left OUT of the object you build (never assigned, `undefined`)
 *   preserves the stored value — `JSON.stringify` drops `undefined` keys,
 *   so simply not setting a field is enough to omit it.
 * - `character_name`/`character_class`/`level` are NEVER nullable — sending
 *   `null` for one of these is a 422 `validation` error from the backend.
 *   Don't send them as `null`; only omit or send a real value.
 * - `character_description`/`character_preferences`/`character_sheet` ARE
 *   nullable — an explicit `null` clears them (resets to `{}` server-side
 *   for the two JSON columns). `character_preferences` REPLACES the stored
 *   object; `character_sheet` MERGES provided keys into the stored one.
 */
export interface CharacterUpdateRequest {
  character_name?: string;
  character_class?: string | null;
  level?: number;
  character_description?: string | null;
  character_preferences?: Record<string, unknown> | null;
  character_sheet?: Record<string, unknown> | null;
}

/** Response for POST /{id}/archive and POST /{id}/restore. */
export interface ArchiveRestoreResponse {
  status: string;
  character_id: string;
  character_name: string;
  is_archived: boolean;
  archived_at: string | null;
  updated_at: string | null;
}

/** Response for PUT /{id}/hp — NOT a `Character`; a small dedicated shape. */
export interface CharacterHPUpdateResponse {
  status: string;
  character_id: string;
  hp: number;
  max_hp: number | null;
  updated_at: string | null;
}

/** Response for PUT /{id}/visibility — NOT a `Character`; a small dedicated shape. */
export interface CharacterVisibilityUpdateResponse {
  status: string;
  character_id: string;
  visibility: CharacterVisibility;
  updated_at: string | null;
}

export interface CharacterListResponse {
  characters: Character[];
  total: number;
}

/** Roster query options; ownership and archive scope remain server-authorized. */
export interface CharacterListOptions extends CharacterRequestOptions {
  includeArchived?: boolean;
}

/** Every method accepts this optional bag; currently only `signal` is used. */
export interface CharacterRequestOptions {
  signal?: AbortSignal;
}

/**
 * Machine-readable error codes the Character contract defines, all carried
 * in the response body as `{"detail": {"message": "...", "code": "..."}}`
 * (the same shape as the auth error contract). Several share an HTTP status
 * (e.g. `duplicate_name`/`active_character_exists`/`lifecycle_conflict` are
 * all 409) — `ApiError.kind` alone cannot distinguish them; use
 * `getCharacterErrorCode` for that.
 */
export type CharacterErrorCode =
  | 'duplicate_name'
  | 'active_character_exists'
  | 'lifecycle_conflict'
  | 'forbidden'
  | 'not_found'
  | 'validation';

/**
 * Extract the Character contract's `detail.code` from a thrown `ApiError`,
 * if present. Returns `null` for a non-`ApiError`, a body with no nested
 * `detail.code`, or a code this contract doesn't define (never throws).
 *
 * @example
 * try {
 *   await characterService.createQuickCharacter(request);
 * } catch (err) {
 *   if (getCharacterErrorCode(err) === 'duplicate_name') {
 *     setInlineConflict('That name is already taken in this campaign.');
 *   }
 * }
 */
export function getCharacterErrorCode(error: unknown): CharacterErrorCode | null {
  if (!(error instanceof ApiError)) return null;
  const body = error.detail;
  if (!body || typeof body !== 'object') return null;
  const nested = (body as Record<string, unknown>).detail;
  if (!nested || typeof nested !== 'object') return null;
  const code = (nested as Record<string, unknown>).code;
  const knownCodes: CharacterErrorCode[] = [
    'duplicate_name',
    'active_character_exists',
    'lifecycle_conflict',
    'forbidden',
    'not_found',
    'validation',
  ];
  return typeof code === 'string' && (knownCodes as string[]).includes(code) ? (code as CharacterErrorCode) : null;
}

export class CharacterService {
  private apiBaseUrl = getApiBaseUrl();

  /**
   * POST /api/characters/create-quick — owner projection, HTTP 201.
   * @throws {ApiError} `not_found` (campaign), `forbidden` (not a participant),
   *   `duplicate_name` (own-active-character or name collision), or network/unavailable.
   */
  async createQuickCharacter(request: CharacterQuickCreateRequest, options?: CharacterRequestOptions): Promise<Character> {
    let response: Response;
    try {
      response = await authorizedFetch(`${this.apiBaseUrl}/characters/create-quick`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
        signal: options?.signal,
      });
    } catch (error) {
      throw apiErrorFromNetworkFailure(error, 'Failed to create character');
    }
    if (!response.ok) throw await apiErrorFromResponse(response, 'Failed to create character');
    return response.json();
  }

  /**
   * GET /api/characters/campaigns/{id}/characters. Each row carries the
   * server-computed `is_owner` flag; order is display-only. Archived rows are
   * included only when requested and only for the authenticated owner.
   *
   * @throws {ApiError} on any non-ok response or network failure — an empty
   *   array is returned only for a genuine 200 with zero characters, never
   *   for a failure.
   */
  async listCharactersByCampaign(campaignId: string, options?: CharacterListOptions): Promise<Character[]> {
    let response: Response;
    try {
      // This flag asks the server for active rows plus only this verified
      // viewer's archives; it never relaxes current campaign membership.
      const query = options?.includeArchived ? '?include_archived=true' : '';
      response = await authorizedFetch(
        `${this.apiBaseUrl}/characters/campaigns/${campaignId}/characters${query}`,
        { method: 'GET', signal: options?.signal }
      );
    } catch (error) {
      throw apiErrorFromNetworkFailure(error, 'Failed to load roster');
    }
    if (!response.ok) throw await apiErrorFromResponse(response, 'Failed to load roster');
    const data: CharacterListResponse = await response.json();
    return data.characters || [];
  }

  /**
   * GET /api/characters/{id}. Owner projection for the owner, filtered
   * projection for everyone else, with explicit server-computed ownership.
   *
   * @throws {ApiError} `not_found` for missing or inaccessible IDs, including
   *   archived rows even for their owner. The server scopes the row before loading it;
   *   this response intentionally cannot distinguish absence from denied scope.
   *   Own-character reads retain the server's owner exception after removal.
   *   Transport/auth failures retain their existing ApiError classification.
   */
  async getCharacter(characterId: string, options?: CharacterRequestOptions): Promise<Character> {
    let response: Response;
    try {
      response = await authorizedFetch(
        `${this.apiBaseUrl}/characters/${characterId}`,
        { method: 'GET', signal: options?.signal }
      );
    } catch (error) {
      throw apiErrorFromNetworkFailure(error, 'Failed to load character');
    }
    if (!response.ok) {
      throw await apiErrorFromResponse(response, response.status === 404 ? 'Character not found' : 'Failed to load character');
    }
    return response.json();
  }

  /**
   * PUT /api/characters/{id} — partial edit. Only send keys that actually
   * changed; see `CharacterUpdateRequest`'s header for omitted-vs-null.
   * Owner-only (or site admin/moderator) — no GM bypass.
   *
   * @throws {ApiError} `not_found`, `forbidden` (not the owner),
   *   `duplicate_name` (new name collides), `lifecycle_conflict`,
   *   `validation` (422 — e.g. explicit null on name/class/level).
   */
  async updateCharacter(characterId: string, updates: CharacterUpdateRequest, options?: CharacterRequestOptions): Promise<Character> {
    let response: Response;
    try {
      response = await authorizedFetch(
        `${this.apiBaseUrl}/characters/${characterId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updates),
          signal: options?.signal,
        }
      );
    } catch (error) {
      throw apiErrorFromNetworkFailure(error, 'Failed to update character');
    }
    if (!response.ok) throw await apiErrorFromResponse(response, 'Failed to update character');
    return response.json();
  }

  /**
   * PUT /api/characters/{id}/hp. Owner-only (GM bypass removed 001A.4).
   * @throws {ApiError} `not_found`, `forbidden`, `lifecycle_conflict`.
   */
  async updateCharacterHP(characterId: string, request: CharacterHPUpdateRequest, options?: CharacterRequestOptions): Promise<CharacterHPUpdateResponse> {
    let response: Response;
    try {
      response = await authorizedFetch(
        `${this.apiBaseUrl}/characters/${characterId}/hp`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(request),
          signal: options?.signal,
        }
      );
    } catch (error) {
      throw apiErrorFromNetworkFailure(error, 'Failed to update HP');
    }
    if (!response.ok) throw await apiErrorFromResponse(response, 'Failed to update HP');
    return response.json();
  }

  /**
   * PUT /api/characters/{id}/visibility. Owner-only.
   * `hp` maps to the backend's stored `hit_points` visibility key — the one
   * real field-name alias in this contract; no other renaming applies.
   * @throws {ApiError} `not_found`, `forbidden`, `lifecycle_conflict`.
   */
  async updateCharacterVisibility(
    characterId: string,
    visibility: CharacterVisibilityRequest,
    options?: CharacterRequestOptions
  ): Promise<CharacterVisibilityUpdateResponse> {
    let response: Response;
    try {
      response = await authorizedFetch(
        `${this.apiBaseUrl}/characters/${characterId}/visibility`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(visibility),
          signal: options?.signal,
        }
      );
    } catch (error) {
      throw apiErrorFromNetworkFailure(error, 'Failed to update visibility');
    }
    if (!response.ok) throw await apiErrorFromResponse(response, 'Failed to update visibility');
    return response.json();
  }

  /**
   * POST /api/characters/{id}/archive — soft delete, recoverable, idempotent.
   * Replaces the old `deleteCharacter`/`DELETE {id}` — that verb is not
   * registered on the backend at all (001A.4 contract). No request body.
   * Never a hard delete; session/membership references are unaffected.
   *
   * @throws {ApiError} `not_found`, `forbidden` (owner-only, no GM bypass),
   *   `lifecycle_conflict`.
   */
  async archiveCharacter(characterId: string, options?: CharacterRequestOptions): Promise<ArchiveRestoreResponse> {
    let response: Response;
    try {
      response = await authorizedFetch(
        `${this.apiBaseUrl}/characters/${characterId}/archive`,
        { method: 'POST', signal: options?.signal }
      );
    } catch (error) {
      throw apiErrorFromNetworkFailure(error, 'Failed to archive character');
    }
    if (!response.ok) throw await apiErrorFromResponse(response, 'Failed to archive character');
    return response.json();
  }

  /**
   * POST /api/characters/{id}/restore. Available on EVERY tier — the
   * Premium-only gate was removed backend-side (001A.4). No request body.
   * Idempotent if already active.
   *
   * The campaign roster's minimal archived list calls this method. The
   * backend verifies current membership and ownership for every request.
   *
   * @throws {ApiError} `not_found` (absent or outside current membership),
   *   `forbidden` (current member is not the owner; verified site admin/moderator
   *   override remains server-owned), `active_character_exists` (caller has a
   *   DIFFERENT active character in this campaign — that character is never
   *   touched), `lifecycle_conflict`.
   */
  async restoreCharacter(characterId: string, options?: CharacterRequestOptions): Promise<ArchiveRestoreResponse> {
    let response: Response;
    try {
      response = await authorizedFetch(
        `${this.apiBaseUrl}/characters/${characterId}/restore`,
        { method: 'POST', signal: options?.signal }
      );
    } catch (error) {
      throw apiErrorFromNetworkFailure(error, 'Failed to restore character');
    }
    if (!response.ok) throw await apiErrorFromResponse(response, 'Failed to restore character');
    return response.json();
  }
}

// Export singleton instance
export const characterService = new CharacterService();
